import jQuery from 'jquery';
let $ = jQuery;
import A11Y from 'HSNDIR/AccessibilityUtility.js';
import Events from 'HSNDIR/Events.js';

/**
    * class HSN.Widget.ComboBox
    *
    * Encapsulates the common functionality of the ComboBox Widget.
    * ComboBox's consist of a <textbox> field (or a <a> / <button> trigger) and a flyout (popup). Keyboard navigation enabled.
    * ComboBox's come in 4 types: ListBox, Grid, Tree, Dialog
    * Currently Supported: ListBox
    * Add support for new one's as you need them
    * // Follows aria practices for accessibility from https://www.w3.org/TR/wai-aria-practices/#combobox
    *    For now, JAWS does not do combobox's so we are rolling our own
**/

HSN.Widget.ComboBox = function () {
    let application;
    let events = new HSN.EventCoordinator();

    let container;
    let button = $();
    let textbox = $();
    let flyout;
    let srText;
    let keys;
    let triggerType;
    let flyoutType;
    let focusedItem;

    let flyoutVisible = false;
    let flyoutItemSelector = 'a, button';

    let srTextTimeout;

    let triggerTypes = {
        button: 1,
        textbox: 2
    };

    let flyoutTypes = {
        listbox: 1
        //grid: 2,
        //tree: 3,
        //dialog: 4
    };

    /**
        * new HSN.Widget.ComboBox.init(config)
        * - config (Object): A configuration object for the [[HSN.Widget.ComboBox]]
        *   consisting of the following keys:
        *   - application (HSN.Application): An instance of the [[HSN.Application]] that the widget should interact with.
        *   - container (HTMLElement | jQuery | String): A jQuery object, HTML element, or CSS selector that will ultimately resolve to an HTML element will act as the root container for the widget.
        *   - textbox (HTMLElement | jQuery | String): A jQuery object, HTML element, or CSS selector of the <input> textbox.
        *   -    OR button (HTMLElement | jQuery | String): A jQuery object, HTML element, or CSS selector of the <a> or <button> element (trigger).
        *   - flyout (HTMLElement | jQuery | String): A jQuery object, HTML element, or CSS selector of the flyout.
        *   - triggerType (enum HSN.Widget.ComboBox.triggerTypes): Declares the type of trigger this combo box has
        *   - flyoutType (enum HSN.Widget.ComboBox.flyoutTypes): Declares the type of flyout this combo box has
        *   - flyoutItemSelector (Optional) (String) - Change the 'item' jQuery selector of flyouts
    **/
    function init(config) {
        // Required
        application = config.application;
        container = $(config.container);
        button = $(config.button);
        textbox = $(config.textbox);
        flyout = $(config.flyout);
        triggerType = config.triggerType;
        flyoutType = config.flyoutType;

        // Optional
        if (config.flyoutItemSelector) {
            flyoutItemSelector = config.flyoutItemSelector;
        }

        // Convenience
        if (!triggerType) {
            if (button) {
                triggerType = triggerTypes.button;
            }
            else if (textbox) {
                triggerType = triggerTypes.textbox;
            }
        }

        // Warnings
        if (triggerType == triggerTypes.button && !button) {
            console.log('Must be a button/trigger passed to config param "button"');
        }
        if (triggerType == triggerTypes.textbox && !textbox) {
            console.log('Must be a textbox passed to config param "textbox"');
        }

        keys = A11Y.GetKeys();
        bindEvents();
    }

    function destroy() {
        textbox.off('keydown', () => textboxKeydown(event));
        flyout.off('keydown', () => flyoutKeydown(event));

        application = container = textbox = flyout = null;
    }

    function getButton() {
        return button;
    }

    function getTextbox() {
        return textbox;
    }

    function getFlyout() {
        return flyout;
    }

    function isFocused() {
        return (button.is(':focus') || textbox.is(':focus') || flyout.find(':focus').length);
    }

    function bindEvents() {
        if (triggerType == triggerTypes.button) {
            button.on('click', (event) => buttonClick(event));
            button.on('keydown', (event) => buttonKeydown(event));
        }
        if (triggerType == triggerTypes.textbox) {
            textbox.on('keydown', (event) => textboxKeydown(event));
        }

        flyout.on('keydown', (event) => flyoutKeydown(event));
    }

    function buttonClick(event) {
        toggleFlyout();

        event.preventDefault();
        event.stopPropagation();
    }

    function buttonKeydown(event) {
        var key = event.keyCode || event.which;
        var caught = true;

        if (flyoutType === flyoutTypes.listbox) {
            switch (key) {
                case keys.enter:
                    events.Trigger(HSN.Widget.ComboBox.EVENT.EXECUTE);
                    break;
                case keys.esc:
                    hideFlyout();
                    break;
                case keys.down:
                    if (flyoutVisible) {
                        focusItem(getItems().first());
                    }
                    break;
                case keys.up:
                    if (flyoutVisible) {
                        focusItem(getItems().last());
                    }
                    break;
                default:
                    caught = false;
                    break;
            }
        }

        if (caught) {
            event.preventDefault();
            event.stopPropagation();
        }

        return true;
    }

    function textboxKeydown(event) {
        var key = event.keyCode || event.which;
        var caught = true;

        if (flyoutType === flyoutTypes.listbox) {
            switch (key) {
                case keys.enter:
                    events.Trigger(HSN.Widget.ComboBox.EVENT.EXECUTE);
                    break;
                case keys.esc:
                    hideFlyout();
                    break;
                case keys.down:
                    if (flyoutVisible) {
                        focusItem(getItems().first());
                    }
                    break;
                case keys.up:
                    if (flyoutVisible) {
                        focusItem(getItems().last());
                    }
                    break;
                default:
                    caught = false;
                    break;
            }
        }

        if (caught) {
            event.preventDefault();
            event.stopPropagation();
        }

        return true;
    }

    function flyoutKeydown(event) {
        var key = event.keyCode || event.which;
        var caught = true;

        if (flyoutType === flyoutTypes.listbox) {
            switch (key) {
                case keys.enter:
                    selectItem(getFocusedItem());
                    break;
                case keys.esc:
                    hideFlyout();
                    if (triggerType == triggerTypes.button) {
                        button.focus();
                    }
                    else if (triggerType == triggerTypes.textbox) {
                        textbox.focus();
                    }
                    break;
                case keys.left:
                    if (triggerType == triggerTypes.textbox) {
                        textboxMoveCaret(-1);
                    }
                    break;
                case keys.right:
                    if (triggerType == triggerTypes.textbox) {
                        textboxMoveCaret(1);
                    }
                    break;
                case keys.tab:
                    if (event.shiftKey) {
                        // Try to select previos item, if not one, let regular shift+tab bring us out of the flyout
                        if (focusPrevItem() != true) {
                            caught = false;
                            break;
                        }
                    }
                    else {
                        // Try to select next item, if not one, let regular tab bring us out of the flyout
                        if (focusNextItem() != true) {
                            caught = false;
                        }
                    }
                    break;
                case keys.down:
                    focusNextItem();
                    break;
                case keys.up:
                    if (event.altKey) {
                        hideFlyout();
                        textbox.focus();
                    }
                    else {
                        focusPrevItem();
                    }
                    break;
                case keys.home:
                    focusItem(getItems().first());
                    break;
                case keys.end:
                    focusItem(getItems().last());
                    break;
                case keys.backspace:
                    // Do nothing
                    break;
                default:
                    caught = false;
                    break;
            }
        }

        if (caught) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    function docKeyUp(event) {
        var key = event.keyCode || event.which;

        if (key != keys.tab) {
            return;
        }

        if (!isFocused()) {
            hideFlyout();
        }
    }

    function textboxMoveCaret(movement) {
        var input = textbox.get(0);

        if (!input || !input.setSelectionRange) {
            return;
        }

        textbox.focus();

        var start = input.selectionStart;
        var end = input.selectionEnd;

        if (movement == -1) {
            if (start <= 0) {
                return;
            }

            start += movement;
            input.setSelectionRange(start, start);
        }
        else if (movement == 1) {
            if (end >= textbox.val().length) {
                return;
            }

            end += movement;
            input.setSelectionRange(end, end);
        }
    }

    function showFlyout(shouldFocus) {
        flyout.show();
        flyoutVisible = true;

        getItems().attr('tabindex', '0');

        if (shouldFocus) {
            focusItem(getItems().first());
        }

        $(document).on('keyup', docKeyUp);
    }

    function hideFlyout() {
        flyout.hide();
        flyoutVisible = false;

        getItems().attr('tabindex', '-1');

        $(document).off('keyup', docKeyUp);
    }

    function toggleFlyout() {
        if (flyoutVisible) {
            hideFlyout();
        }
        else {
            events.Trigger(HSN.Widget.ComboBox.EVENT.EXECUTE);
        }
    }

    function focusItem(item) {
        if (triggerType == triggerTypes.button) {
            button.attr('aria-activedescendant', item.attr('id'));
        }
        else if (triggerType == triggerTypes.textbox) {
            textbox.attr('aria-activedescendant', item.attr('id'));
        }
        
        focusedItem = item;
        item.focus();
        events.Trigger(HSN.Widget.ComboBox.EVENT.ITEMFOCUSED);
    }

    function selectItem(item) {
        focusItem(item);
        getItems().removeClass('selected')
            .attr('aria-selected', 'false');
        item.addClass('selected')
            .attr('aria-selected', 'true');
        events.Trigger(HSN.Widget.ComboBox.EVENT.ITEMSELECTED);
    }

    function getItems() {
        return flyout.find(flyoutItemSelector);
    }

    function getFocusedItem() {
        if (focusedItem) {
            return focusedItem;
        }
        else {
            return getItems().filter(':focus');
        }
    }

    function focusNextItem() {
        var items = getItems();
        var cur = items.filter(':focus');
        var index = items.index(cur);

        if (index < items.length - 1) {
            focusItem(items.eq(index + 1));
            return true;
        }
        else {
            return false;
        }
    }

    function focusPrevItem() {
        var items = getItems();
        var cur = items.filter(':focus');
        var index = items.index(cur);

        if (index > 0) {
            focusItem(items.eq(index - 1));
            return true;
        }
        else {
            return false;
        }
    }

    function say(text) {
        prepSay();

        clearTimeout(srTextTimeout);
        srTextTimeout = setTimeout(function () {
            srText.text('');
        }, 150);

        srText.text(text);
    }

    // This is basically here to allow multiple combobox's to share a container if needed
    function prepSay() {
        var existing = container.find('screenreader-text').eq(0);

        if (!srText || !srText.length) {
            if (existing.length) {
                srText = existing;
            }
            else {
                srText = $('<div aria-live="assertive" class="screenreader-text visuallyhidden"></div>');
                container.append(srText);
            }
        }
    }

    return {
        init: init,
        destroy: destroy,
        events: events,
        getButton: getButton,
        getTextbox: getTextbox,
        getFlyout: getFlyout,
        getItems: getItems,
        getFocusedItem: getFocusedItem,
        focusItem: focusItem,
        selectItem: selectItem,
        showFlyout: showFlyout,
        hideFlyout: hideFlyout,
        triggerTypes: triggerTypes,
        flyoutTypes: flyoutTypes,
        isFocused: isFocused,
        say: say
    };
};

HSN.Widget.ComboBox.EVENT = {
    ACTIVATE: 'HSN.Widget.ComboBox:ACTIVATE',
    DESTORY: 'HSN.Widget.ComboBox:DESTORY',
    EXECUTE: 'HSN.Widget.ComboBox:EXECUTE',
    ITEMFOCUSED: 'HSN.Widget.ComboBox:ITEMFOCUSED',
    ITEMSELECTED: 'HSN.Widget.ComboBox:ITEMSELECTED'
};


// WEBPACK FOOTER //
// ./assets/js/HSN/Widget/ComboBox.js